/**
 * CAR-1 — Conservative bearing-capacity inference from USCS soil class.
 *
 * Pack: docs/prompt-packs/Coverage_AutoFill_Realworld_Research_Prompt.md (§CAR-1).
 *
 * The Author Evidence dialog for the `geotech` requirement
 * (`src/config/documentChecklists.ts:85`) expects a numeric bearing
 * capacity in kPa. SSURGO does NOT publish bearing capacity directly —
 * it publishes USDA texture and engineering properties. This helper
 * translates a USCS class (already derived from USDA texture in
 * `nrcsSoilSurvey.ts`) into a CONSERVATIVE bearing-capacity estimate
 * for a 1.5m embedded shallow foundation, optionally corrected for a
 * shallow water table.
 *
 * Reference physics (project rule §2 — preserve exact physics):
 *
 *   - The base ranges come from typical-values tables in geotechnical
 *     practice: NRCS NSSH Table 423-A.5 + Bowles 1996 Foundation
 *     Analysis & Design Table 4-3 + Coduto 2001 Foundation Design
 *     Table 7-1. They reflect the LOWER-bound presumptive bearing
 *     pressure a designer would use for preliminary sizing BEFORE a
 *     site-specific geotechnical investigation. They are NOT a
 *     substitute for a Terzaghi or Meyerhof analysis with measured
 *     shear-strength parameters.
 *
 *   - Groundwater correction (Terzaghi 1943, "buoyant" reduction):
 *     when the water table is within the failure zone (commonly taken
 *     as 1.5× footing width below founding level, i.e. ~ 1.5 m for a
 *     shallow foundation), the effective unit weight is reduced by
 *     ~50% and the ultimate bearing capacity drops by approximately
 *     the same factor. We apply a flat 0.5 reduction when `gw < 1.5m`
 *     per Coduto §7.4. This is itself an approximation — a full
 *     Terzaghi correction integrates over the failure surface — but
 *     it bounds the conservative end of the range.
 *
 * Hallucination guard (HARD RULE 8):
 *   - The output `rationale` always names the source (USCS class + the
 *     NRCS / Terzaghi citation chain) so the AuthorEvidenceDialog can
 *     surface a citation chip in the dialog's notes line.
 *
 * Out of scope:
 *   - Deep foundations (piles, drilled shafts) — different equations.
 *     The pack scopes CAR-1 to shallow foundations only.
 *   - Per-state geotechnical correlations from highway-DOT borehole
 *     databases (e.g. Arkansas Geological Survey) — pack §CAR-1
 *     "State Geological Survey APIs" calls these out as a CAR-1-
 *     FOLLOWUP.
 */
import type { UscsClass } from '../../services/dataProviders/nrcsSoilSurvey';

/**
 * Typical presumptive bearing capacity ranges, kPa, for a 1.5 m deep
 * shallow foundation with the water table well below the failure
 * zone (>1.5 m). Each entry is [low, high]; the helper returns the
 * MIDPOINT as a single value with a confidence band.
 *
 * Numbers reproduced from the curated DAM entries `geotech-001` and
 * `geotech-002` (pack §CAR-1 DAM additions). Re-stating the table
 * inline here keeps the helper self-contained for unit testing — the
 * DAM is the canonical source of truth for the report-time citation.
 */
export const USCS_BEARING_RANGE_KPA: Record<UscsClass, readonly [number, number]> = {
  SP: [150, 300], // poorly graded sand
  SM: [100, 200], // silty sand
  SC: [100, 180], // clayey sand
  ML: [75, 150],  // silt / silt loam
  CL: [75, 150],  // lean clay
  CH: [50, 100],  // fat clay
  OL: [25, 50],   // organic silt — flag for over-excavation
  OH: [25, 50],   // organic clay — flag for over-excavation
  Pt: [10, 30],   // peat — flag as unsuitable for foundations
};

/**
 * Groundwater depth (m) below which we apply NO water-table reduction.
 * Below this depth the failure zone for a 1.5 m shallow foundation is
 * entirely above the water table, so effective unit weight is the
 * total unit weight. Source: Coduto 2001 §7.4 (depth-to-water-table
 * reduction rule of thumb).
 */
export const GW_INFLUENCE_DEPTH_M = 1.5;

/**
 * Multiplicative reduction applied to the bearing-capacity midpoint
 * when the water table is at or above the founding level. 0.5 is the
 * standard "buoyant" approximation — see header comment.
 */
export const GW_BUOYANT_REDUCTION = 0.5;

/** Inputs to {@link inferBearingCapacityKPa}. */
export interface InferBearingCapacityInput {
  uscsClass: UscsClass;
  /** Groundwater depth in metres below ground surface. Null when
   *  unknown — the helper then DOESN'T apply the buoyant reduction and
   *  flags the gap in the rationale. */
  groundwaterDepthM: number | null;
}

/**
 * Confidence band for the bearing-capacity estimate, mirroring the
 * `inferAutoFillConfidence` taxonomy. Numeric values match
 * `AUTHORED_CONFIDENCE_SCORE` so a Coverage Matrix row can render a
 * single number that means the same thing across authored / autofilled
 * paths.
 */
export type BearingCapacityConfidence = 0.45 | 0.6 | 0.75;

export interface InferBearingCapacityResult {
  /** Rounded conservative kPa estimate for the 1.5 m shallow
   *  foundation case. */
  value: number;
  /** Confidence band. `0.45` = low (USCS-only, no GW data),
   *  `0.60` = medium (USCS + GW data, allows buoyant correction).
   *  Never `0.75` — high requires a real boring log. */
  confidence: BearingCapacityConfidence;
  /** Human-readable rationale that the AuthorEvidenceDialog notes
   *  textarea quotes verbatim. Always cites the source chain. */
  rationale: string;
}

/**
 * Compute the conservative bearing-capacity estimate for a USCS class,
 * optionally corrected for a shallow water table.
 *
 * @param input  USCS class and optional groundwater depth.
 * @returns      Rounded value (kPa) + confidence band + rationale.
 */
export function inferBearingCapacityKPa(
  input: InferBearingCapacityInput,
): InferBearingCapacityResult {
  const [lo, hi] = USCS_BEARING_RANGE_KPA[input.uscsClass];
  const midpoint = (lo + hi) / 2;
  const gw = input.groundwaterDepthM;

  // Apply buoyant reduction when water table is within the failure
  // zone of a 1.5 m shallow foundation.
  const corrected =
    gw != null && gw < GW_INFLUENCE_DEPTH_M ? midpoint * GW_BUOYANT_REDUCTION : midpoint;
  // The output goes into the AuthorEvidenceDialog's numeric input —
  // round to the nearest integer kPa to match what a designer would
  // record in a feasibility-stage table.
  const value = Math.round(corrected);

  // Confidence ladder per pack §CAR-1 "Author Evidence dialog auto-
  // population": GW data bumps low → medium. High is reserved for a
  // real boring log (the dialog elevates to 'high' when the user
  // uploads a Phase II geotechnical report).
  const confidence: BearingCapacityConfidence = gw != null ? 0.6 : 0.45;

  const rationaleParts: string[] = [];
  rationaleParts.push(
    `USCS ${input.uscsClass}: typical 1.5 m shallow foundation bearing capacity ` +
      `${lo}-${hi} kPa (NRCS NSSH Table 423-A.5 + Bowles 1996 Table 4-3).`,
  );
  if (gw != null) {
    if (gw < GW_INFLUENCE_DEPTH_M) {
      rationaleParts.push(
        `Water table at ${gw.toFixed(1)} m — within failure zone; ` +
          `applying 50% buoyant reduction per Coduto 2001 §7.4. ` +
          `Effective ${value} kPa.`,
      );
    } else {
      rationaleParts.push(
        `Water table at ${gw.toFixed(1)} m — below failure zone; ` +
          `no reduction applied. Midpoint ${value} kPa.`,
      );
    }
  } else {
    rationaleParts.push(
      `Groundwater depth unknown — dry-condition midpoint ${value} kPa. ` +
        `Confirm groundwater elevation before final foundation sizing.`,
    );
  }
  if (input.uscsClass === 'OL' || input.uscsClass === 'OH') {
    rationaleParts.push(
      'Organic soil — over-excavation and replacement with engineered ' +
        'fill is typically required for non-residential structures.',
    );
  }
  if (input.uscsClass === 'Pt') {
    rationaleParts.push(
      'Peat — unsuitable for shallow foundations; deep foundations or ' +
        'full subgrade replacement required.',
    );
  }

  return {
    value,
    confidence,
    rationale: rationaleParts.join(' '),
  };
}
